import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AppStateService } from '../../services/app-state.service';

interface MonthGrowthDetail {
  month: number;
  value: number;
  description: string;
  status: string;
}

@Component({
  selector: 'app-advantages',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './advantages.component.html'
})
export class AdvantagesComponent implements OnInit, OnDestroy {
  public state = inject(AppStateService);
  private simulationInterval: any;

  // 1. LP Split Calculator State
  public totalFee = signal<number>(5000);
  public tokenTvlSplit = computed(() => this.totalFee() * 0.3);
  public dexV3Split = computed(() => this.totalFee() * 0.7);

  // 2. Compounding Growth State (5 Months handover)
  public selectedMonth = signal<number>(5);
  public growthData = signal<MonthGrowthDetail[]>([
    {
      month: 1,
      value: 3500,
      description: 'Initial LP Position setup from the $3,500 Split Fee injection into the DEX ETH/USDC V3 Pool.',
      status: 'Initial Deposit'
    },
    {
      month: 2,
      value: 6800,
      description: 'Daily fee accruals from high-volume trading begin compounding automatically into the pool.',
      status: 'Compounding Active'
    },
    {
      month: 3,
      value: 12400,
      description: 'Earning velocity increases as concentrated liquidity parameters optimize. DEX rewards boost APY.',
      status: 'Accelerated Yield'
    },
    {
      month: 4,
      value: 20100,
      description: 'Re-investment auto-triggers compound cycles. Capital efficiency is multiplied 2.5x to 5x.',
      status: 'Supercharged Compounding'
    },
    {
      month: 5,
      value: 30000,
      description: 'Growth target met. LP Position handed over to the Token Creator to become an independent Crypto Micro Cap Fund.',
      status: 'Fund Handover & Autonomy'
    }
  ]);

  public activeGrowthDetail = computed(() => {
    const data = this.growthData();
    const month = this.selectedMonth();
    return data.find(d => d.month === month) || data[4];
  });

  // 3. Concentrated Liquidity Range State
  public activeRangeMode = signal<string>('balanced');
  public rangeConfig = computed(() => {
    switch (this.activeRangeMode()) {
      case 'narrow':
        return {
          title: 'Narrow Price Band (V3)',
          rangeText: '± 2.5%',
          multiplier: '5.0x',
          baseApy: '118.5%',
          riskLevel: 'High Impermanent Loss Risk',
          riskColor: 'text-red-400 bg-red-500/10 border-red-500/25',
          description: 'Focuses liquidity tightly around current price. Maximum fees, but requires active adjustment if market moves.'
        };
      case 'wide':
        return {
          title: 'Wide Price Band (V3)',
          rangeText: '± 25.0%',
          multiplier: '1.2x',
          baseApy: '28.2%',
          riskLevel: 'Low Impermanent Loss Risk',
          riskColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
          description: 'Broad price coverage. Safe, set-and-forget strategy with stable, passive yield generation across swings.'
        };
      case 'balanced':
      default:
        return {
          title: 'Balanced Price Band (V3)',
          rangeText: '± 8.0%',
          multiplier: '2.8x',
          baseApy: '64.8%',
          riskLevel: 'Medium Impermanent Loss Risk',
          riskColor: 'text-gold bg-gold/10 border-gold/25',
          description: 'Optimal balance. Captures standard market fluctuations without excessive exposure or rebalancing needs.'
        };
    }
  });

  // 4. Live Simulation Counter
  public simAccumulatedFees = signal<number>(245.8921);
  public simDexRewards = signal<number>(1048.5132);
  public isReinvesting = signal<boolean>(true);
  public totalSimCompounded = signal<number>(4500);

  // 5. Learning Hub Tab State
  public activeAcademyTopic = signal<string>('strategies');
  public academyTopics = signal([
    {
      id: 'academy',
      title: 'Academy & Tutorials',
      short: 'Beginner guides & core conceptual modules.',
      full: 'New to DeFi or launching a token? Our structured academy teaches you the fundamentals of tokenomics, concentrated liquidity provisions, and auditing parameters. Understand how decentralized liquidity behaves and how to bootstrap your project secure and efficient.',
      videoUrl: '/videos/dex-academy-tutorials.mp4'
    },
    {
      id: 'risk',
      title: 'Risk Management',
      short: 'Techniques for hedging and impermanent loss control.',
      full: 'Yield is nothing without safety. Learn how to configure custom hedging strategies, set stop-losses, deploy range buffers, and evaluate historical volatility. Discover how DEX safeguards your TVL pool through automatic rebalancing recommendations.',
      videoUrl: '/videos/dex-risk-management.mp4'
    },
    {
      id: 'strategies',
      title: 'Concentrated Liquidity',
      short: 'Unlocking maximum capital efficiency (V3).',
      full: 'Concentrated liquidity (V3) lets you allocate capital within specific price ranges. By concentrating tokens where trading occurs, you capture up to 5x-10x more fee share than standard V2 pools. DEX provides custom templates tailored for new launch partners.',
      videoUrl: '/videos/dex-concentrated-liquidity.mp4'
    }
  ]);

  public currentAcademyContent = computed(() => {
    return this.academyTopics().find(t => t.id === this.activeAcademyTopic()) || this.academyTopics()[2];
  });

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
    }
  }

  // Interactive functions
  onFeeSliderChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.totalFee.set(Number(target.value));
    }
  }

  selectMonth(month: number) {
    this.selectedMonth.set(month);
  }

  setRangeMode(mode: string) {
    this.activeRangeMode.set(mode);
  }

  toggleReinvest() {
    this.isReinvesting.update(v => !v);
    this.state.triggerNotification(
      this.isReinvesting() 
        ? 'Auto-Reinvestment enabled! Daily fees are now compounding.' 
        : 'Auto-Reinvestment paused. Fees will accumulate in cash.'
    );
  }

  setAcademyTopic(topic: string) {
    this.activeAcademyTopic.set(topic);
  }

  claimSimulatedRewards() {
    const fees = this.simAccumulatedFees();
    const rewards = this.simDexRewards();
    this.state.triggerNotification(
      `Claimed simulated ${fees.toFixed(4)} ETH/USDC fees and ${rewards.toFixed(4)} DEX to wallet!`
    );
    this.simAccumulatedFees.set(0.00);
    this.simDexRewards.set(0.00);
  }
}
